import React, { useState, useEffect, useRef } from 'react';
import { IoChevronDown, IoShareSocial, IoShareSocialSharp } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { BsEmojiSmile } from 'react-icons/bs';
import { useAuth } from 'context/AuthContext';
import {
  registrarElogio,
  registrarFavorito,
  registrarCompartilhamento,
} from 'services/iniciativa-action.service';

interface IniciativaActionBarProps {
  id: string;
  titulo: string;
  descricao: string;
  totalElogios: number;
  totalCompartilhamentos: number;
  totalFavoritos: number;
  usuarioElogio?: string;
  usuarioFavoritou: boolean;
  usuarioCompartilhou: boolean;
}

const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

export const IniciativaActionBar: React.FC<IniciativaActionBarProps> = ({
  id,
  titulo,
  descricao,
  totalElogios,
  totalCompartilhamentos,
  totalFavoritos,
  usuarioElogio,
  usuarioFavoritou,
  usuarioCompartilhou,
}) => {
  const { user } = useAuth();
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(usuarioElogio || '');
  const [isFavorited, setIsFavorited] = useState(usuarioFavoritou);
  const [isShared, setIsShared] = useState(usuarioCompartilhou);
  const [loadingAction, setLoadingAction] = useState(false);
  const complimentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };
    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isComplimentOpen]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;
    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);
      await registrarElogio(user._id, { id_iniciativa: id, elogio: compliment });
    } catch {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;
    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;
      await registrarFavorito(user._id, { id_iniciativa: id, ativo: novoStatus });
      setIsFavorited(novoStatus);
    } catch {
      // silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const handleShare = async () => {
    if (!user?._id || loadingAction) return;
    try {
      setLoadingAction(true);
      await registrarCompartilhamento(user._id, { id_iniciativa: id });
      setIsShared(true);
      if (navigator.share) {
        await navigator.share({ title: titulo, text: descricao, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copiado para a área de transferência!');
      }
    } catch {
      // silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const activeColor = '#5D5FEF';
  const inactiveColor = '#666666';

  return (
    <div className="flex items-center justify-between px-5 py-3.5 bg-white border border-gray-200 rounded-xl">
      {/* Esquerda: elogios */}
      <div className="flex items-center gap-2.5">
        <div className="relative flex items-center">
          <BsEmojiSmile size={20} style={{ color: selectedCompliment ? activeColor : inactiveColor }} />
          {totalElogios > 0 && (
            <span className="absolute -top-1.5 -right-2 bg-red-600 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-bold">
              {totalElogios}
            </span>
          )}
        </div>

        <div className="relative" ref={complimentRef}>
          <div
            className="flex items-center gap-1.5 border rounded-full px-3 py-1.5 text-sm select-none min-w-[160px]"
            onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
            style={{
              opacity: loadingAction ? 0.6 : 1,
              cursor: loadingAction ? 'not-allowed' : 'pointer',
              borderColor: selectedCompliment ? activeColor : '#E0E7FF',
              color: selectedCompliment ? activeColor : inactiveColor,
            }}
          >
            <span>{selectedCompliment || 'Faça um elogio!'}</span>
            <IoChevronDown size={16} />
          </div>
          {isComplimentOpen && !loadingAction && (
            <div className="absolute top-[calc(100%+6px)] left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-[100] min-w-[200px] overflow-hidden">
              {complimentOptions.map((compliment) => (
                <button
                  key={compliment}
                  className="block w-full px-3.5 py-2.5 bg-transparent border-0 text-left text-sm text-gray-700 cursor-pointer hover:bg-indigo-50 hover:text-[#5D5FEF] transition-colors"
                  onClick={() => handleComplimentSelect(compliment)}
                >
                  {compliment}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Direita: compartilhar + favoritar */}
      <div className="flex items-center gap-3">
        {/* <div className="relative flex items-center">
          <button
            className="bg-transparent border-0 cursor-pointer p-1 flex items-center transition-colors"
            title="Compartilhar"
            onClick={handleShare}
            disabled={loadingAction}
            style={{ opacity: loadingAction ? 0.6 : 1, color: isShared ? activeColor : inactiveColor }}
          >
            {isShared ? <IoShareSocialSharp size={20} /> : <IoShareSocial size={20} />}
          </button>
          {totalCompartilhamentos > 0 && (
            <span className="absolute -top-1.5 -right-2 bg-red-600 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-bold">
              {totalCompartilhamentos}
            </span>
          )}
        </div> */}

        <div className="relative flex items-center">
          <button
            className="bg-transparent border-0 cursor-pointer p-1 flex items-center transition-colors"
            title={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
            onClick={handleToggleFavorite}
            disabled={loadingAction}
            style={{ opacity: loadingAction ? 0.6 : 1, color: isFavorited ? activeColor : inactiveColor }}
          >
            {isFavorited ? <MdBookmark size={20} /> : <MdBookmarkBorder size={20} />}
          </button>
          {totalFavoritos > 0 && (
            <span className="absolute -top-1.5 -right-2 bg-red-600 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-bold">
              {totalFavoritos}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
